import { useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent } from "react";
import { phaseAuthHeaders, type SessionUser } from "./auth-client";

type CalcSnapshot = {
  indicator: string;
  value: number | null;
  threshold: number;
  semaforo: string;
  regola_applicata: string;
  flag_dati: string[];
} | null;

export type AssistantContext = {
  kind: "general" | "adeguatezza" | "indebitamento" | "documento";
  clienteId: string;
  clienteName: string;
  contextType: "blurb" | "fixture_demo" | "documento" | "free";
  contextRef?: string;
  title: string;
  value?: CalcSnapshot;
};

type Props = {
  user: SessionUser;
  context: AssistantContext;
  open: boolean;
  onOpen: (context?: Partial<AssistantContext>) => void;
  onClose: () => void;
};

type AssistantTool = "traccia" | "documenti" | null;
type ObservationType = "soglia" | "fattore_mitigante" | "linguaggio" | "scope" | "documento" | "altro";
type ChatMessage = { role: "user" | "assistant"; content: string; provider?: string };

const MAX_TEXT_CHARS = 7000;

const assistantActionClass =
  "px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 text-sm hover:bg-slate-100 dark:hover:bg-slate-800";
const assistantPrimaryActionClass =
  "px-3 py-1.5 rounded border border-cyan-700 bg-cyan-950/40 text-sm text-cyan-50 hover:bg-cyan-900";

function formatValue(context: AssistantContext) {
  if (!context.value) return "nessun calcolo selezionato";
  const value = context.value.value === null ? "non calcolabile" : context.value.value.toFixed(4);
  const flags = context.value.flag_dati.length > 0 ? `; flag: ${context.value.flag_dati.join(", ")}` : "";
  return `${value}, semaforo ${context.value.semaforo}, soglia ${context.value.threshold}${flags}`;
}

function defaultDraft(context: AssistantContext) {
  if (context.kind === "adeguatezza") {
    return "Osservazione su adeguatezza patrimoniale: ";
  }
  if (context.kind === "indebitamento") {
    return "Osservazione su indebitamento finanziario: ";
  }
  if (context.kind === "documento") {
    return "Documento utile per la valutazione: ";
  }
  return "";
}

async function readFileForIntake(file: File) {
  const textLike =
    file.type.startsWith("text/") ||
    ["application/json", "application/xml", "text/csv"].includes(file.type) ||
    /\.(txt|md|csv|json|xml|yaml|yml)$/i.test(file.name);

  if (!textLike) {
    return {
      readable: false,
      excerpt: "Documento binario: contenuto non estratto nel browser in questa versione.",
    };
  }

  const text = await file.text();
  return {
    readable: true,
    excerpt: text.slice(0, MAX_TEXT_CHARS),
  };
}

export function ProraAssistant({ user, context, open, onOpen, onClose }: Props) {
  const [activeTool, setActiveTool] = useState<AssistantTool>(null);
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [docNote, setDocNote] = useState("");
  const [docFile, setDocFile] = useState<File | null>(null);
  const [question, setQuestion] = useState("");
  const [chatStatus, setChatStatus] = useState<"idle" | "sending" | "error">("idle");
  const [chatError, setChatError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      content: "Chiedimi cosa vuoi chiarire o migliorare.",
    },
  ]);
  const [modalWidth, setModalWidth] = useState(1160);
  const [modalHeight, setModalHeight] = useState(640);
  const [modalPosition, setModalPosition] = useState({ x: 260, y: 90 });
  const [leftPct, setLeftPct] = useState(34);
  const dragStart = useRef<{ x: number; pct: number } | null>(null);
  const moveStart = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  const contextLine = useMemo(() => formatValue(context), [context]);
  const initialAssistantMessage: ChatMessage = {
    role: "assistant",
    content: "Chiedimi cosa vuoi chiarire o migliorare.",
  };

  function openAssistant(patch?: Partial<AssistantContext>) {
    onOpen(patch);
    setActiveTool(null);
  }

  function openTool(nextTool: Exclude<AssistantTool, null>, patch?: Partial<AssistantContext>) {
    onOpen(patch);
    setActiveTool(nextTool);
    if (nextTool === "traccia" && draft.trim().length === 0) setDraft(defaultDraft({ ...context, ...patch }));
  }

  function startDividerDrag(e: PointerEvent<HTMLDivElement>) {
    dragStart.current = { x: e.clientX, pct: leftPct };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function moveDivider(e: PointerEvent<HTMLDivElement>) {
    if (!dragStart.current) return;
    const next = dragStart.current.pct + ((e.clientX - dragStart.current.x) / Math.max(modalWidth, 1)) * 100;
    setLeftPct(Math.min(58, Math.max(28, next)));
  }

  function endDividerDrag(e: PointerEvent<HTMLDivElement>) {
    dragStart.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }

  function startModalMove(e: PointerEvent<HTMLDivElement>) {
    moveStart.current = { x: e.clientX, y: e.clientY, left: modalPosition.x, top: modalPosition.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function moveModal(e: PointerEvent<HTMLDivElement>) {
    if (!moveStart.current) return;
    const nextX = moveStart.current.left + e.clientX - moveStart.current.x;
    const nextY = moveStart.current.top + e.clientY - moveStart.current.y;
    setModalPosition({
      x: Math.max(12, Math.min(window.innerWidth - 120, nextX)),
      y: Math.max(12, Math.min(window.innerHeight - 80, nextY)),
    });
  }

  function endModalMove(e: PointerEvent<HTMLDivElement>) {
    moveStart.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }

  async function askAssistant(raw?: string) {
    const clean = (raw ?? question).trim();
    if (clean.length < 2) return;
    setQuestion("");
    setChatStatus("sending");
    setChatError(null);
    setMessages((prev) => [...prev, { role: "user", content: clean }]);
    try {
      const r = await fetch("/api/assistant", {
        method: "POST",
        headers: { "content-type": "application/json", ...phaseAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ question: clean, context }),
      });
      const data = (await r.json()) as { answer?: string; provider?: string; error?: string };
      if (!r.ok || !data.answer) {
        setChatError(data.error || `HTTP ${r.status}`);
        setChatStatus("error");
        return;
      }
      setMessages((prev) => [...prev, { role: "assistant", content: data.answer ?? "", provider: data.provider }]);
      setChatStatus("idle");
    } catch (e) {
      setChatError(e instanceof Error ? e.message : String(e));
      setChatStatus("error");
    }
  }

  async function submitFeedback(kind: ObservationType, content: string, contextPatch?: Partial<AssistantContext>) {
    setStatus("sending");
    setError(null);
    const target = { ...context, ...contextPatch };
    try {
      const r = await fetch("/api/feedback", {
        method: "POST",
        headers: { "content-type": "application/json", ...phaseAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({
          contextType: target.contextType,
          contextRef: target.contextRef ?? `${target.kind}/${target.clienteId}`,
          observationType: kind,
          rilevanza: kind === "documento" ? "alta" : "media",
          content,
        }),
      });
      if (!r.ok) {
        const text = await r.text();
        setError(text || `HTTP ${r.status}`);
        setStatus("error");
        return false;
      }
      setStatus("ok");
      setTimeout(() => setStatus("idle"), 1600);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus("error");
      return false;
    }
  }

  async function submitTrace() {
    const clean = draft.trim();
    if (clean.length < 3) return;
    const content = [
      `Cliente: ${context.clienteName}`,
      `Contesto: ${context.title}`,
      `Dato corrente: ${contextLine}`,
      "",
      clean,
    ].join("\n");
    const ok = await submitFeedback("altro", content);
    if (ok) {
      setDraft("");
      setActiveTool(null);
    }
  }

  async function submitDocument() {
    if (!docFile) {
      setError("Seleziona un documento.");
      return;
    }
    const extracted = await readFileForIntake(docFile);
    const content = [
      `Documento caricato per intake esperto`,
      `Cliente: ${context.clienteName}`,
      `File: ${docFile.name}`,
      `Tipo MIME: ${docFile.type || "non dichiarato"}`,
      `Dimensione: ${docFile.size} byte`,
      `Testo estratto nel browser: ${extracted.readable ? "sì" : "no"}`,
      docNote.trim() ? `Nota: ${docNote.trim()}` : "Nota: n/d",
      "",
      "Estratto / contenuto disponibile:",
      extracted.excerpt,
    ].join("\n");
    const ok = await submitFeedback("documento", content, {
      kind: "documento",
      contextType: "documento",
      contextRef: `documento/${context.clienteId}/${docFile.name}`.slice(0, 200),
      title: "Documento esperto",
    });
    if (ok) {
      setDocFile(null);
      setDocNote("");
      setActiveTool(null);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => openAssistant()}
        className="no-print fixed bottom-6 right-6 z-40 rounded-full border border-sky-300 dark:border-cyan-700 bg-slate-950 text-white dark:bg-cyan-950 dark:text-cyan-50 px-4 py-3 text-sm shadow-lg hover:bg-slate-800 dark:hover:bg-cyan-900 transition-colors"
      >
        PRORA assistente
      </button>

      {open && (
        <div className="no-print fixed inset-0 z-50 bg-slate-950/45 px-3 py-4 md:px-4 md:py-6">
          <div
            className="absolute w-full max-w-[calc(100vw-1.5rem)] md:max-w-[calc(100vw-3rem)] h-[88vh] md:h-auto max-h-[92vh] overflow-hidden rounded-lg border border-slate-300 dark:border-cyan-800 bg-white dark:bg-slate-900 shadow-2xl flex flex-col"
            style={{
              width: `min(${modalWidth}px, calc(100vw - 3rem))`,
              height: `min(${modalHeight}px, 92vh)`,
              left: `${modalPosition.x}px`,
              top: `${modalPosition.y}px`,
              resize: "both",
            }}
            onMouseUp={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setModalWidth(Math.round(rect.width));
              setModalHeight(Math.round(rect.height));
            }}
          >
            <div
              className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-4 cursor-move"
              onPointerDown={startModalMove}
              onPointerMove={moveModal}
              onPointerUp={endModalMove}
              onPointerCancel={endModalMove}
            >
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">PRORA assistant</div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{context.title}</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{context.clienteName} · {contextLine}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => {
                    setMessages([initialAssistantMessage]);
                    setQuestion("");
                    setChatError(null);
                    setChatStatus("idle");
                  }}
                  className="h-9 px-3 rounded border border-slate-300 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  Pulisci
                </button>
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={onClose}
                  className="w-9 h-9 rounded border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label="chiudi assistente"
                >
                  ×
                </button>
              </div>
            </div>

            <div
              className="grid md:grid-cols-[var(--left-panel)_8px_minmax(0,1fr)] min-h-0 flex-1"
              style={{ "--left-panel": `${leftPct}%` } as CSSProperties & Record<"--left-panel", string>}
            >
              <aside className="border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 p-5 space-y-4 overflow-auto">
                <div className="rounded border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Report aperto</div>
                  <div className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">{context.clienteName}</div>
                  <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">{context.title}</div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded border border-slate-200 dark:border-slate-800 p-2">
                      <div className="text-slate-500 dark:text-slate-400">Valore</div>
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {context.value?.value === null ? "non calcolabile" : context.value?.value?.toFixed(4) ?? "n/d"}
                      </div>
                    </div>
                    <div className="rounded border border-slate-200 dark:border-slate-800 p-2">
                      <div className="text-slate-500 dark:text-slate-400">Semaforo</div>
                      <div className="font-medium text-slate-900 dark:text-slate-100">{context.value?.semaforo ?? "n/d"}</div>
                    </div>
                    <div className="rounded border border-slate-200 dark:border-slate-800 p-2">
                      <div className="text-slate-500 dark:text-slate-400">Soglia</div>
                      <div className="font-medium text-slate-900 dark:text-slate-100">{context.value?.threshold ?? "n/d"}</div>
                    </div>
                    <div className="rounded border border-slate-200 dark:border-slate-800 p-2">
                      <div className="text-slate-500 dark:text-slate-400">Tipo</div>
                      <div className="font-medium text-slate-900 dark:text-slate-100">{context.contextType}</div>
                    </div>
                  </div>
                </div>
                <div className="rounded border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 p-4">
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Cosa chiedere o salvare</div>
                  <ul className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-300">
                    <li>Chiarire il significato del blocco-report.</li>
                    <li>Validare soglie e assunzioni segnate come provvisorie.</li>
                    <li>Allegare documenti che il bilancio sintetico non mostra.</li>
                  </ul>
                </div>
              </aside>

              <div
                role="separator"
                aria-label="ridimensiona pannelli assistente"
                className="hidden md:block cursor-col-resize bg-slate-200 dark:bg-slate-800 hover:bg-cyan-500 dark:hover:bg-cyan-700 transition-colors"
                onPointerDown={startDividerDrag}
                onPointerMove={moveDivider}
                onPointerUp={endDividerDrag}
                onPointerCancel={endDividerDrag}
              />

              <section className="min-h-0 flex flex-col">
                <div className="p-5 overflow-auto min-h-0 flex-1">
                  <div className="min-h-full flex flex-col gap-4">
                    <div className="flex-1 min-h-[210px] overflow-auto rounded border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 p-4 space-y-3">
                      {messages.map((msg, idx) => (
                        <div
                          key={`${msg.role}-${idx}`}
                          className={`rounded px-3 py-2 text-sm whitespace-pre-wrap ${
                            msg.role === "user"
                              ? "ml-auto max-w-[88%] bg-slate-900 text-white dark:bg-cyan-900"
                              : "max-w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200"
                          }`}
                        >
                          {msg.content}
                          {msg.provider && msg.role === "assistant" && (
                            <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">provider: {msg.provider}</div>
                          )}
                        </div>
                      ))}
                      {chatStatus === "sending" && (
                        <div className="text-sm text-slate-500 dark:text-slate-400">PRORA sta rispondendo...</div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => askAssistant("Spiegami questo indicatore e cosa devo controllare.")} className={assistantActionClass}>
                          Spiega indicatore
                        </button>
                        <button onClick={() => askAssistant("Quali documenti servono per migliorare questa valutazione?")} className={assistantActionClass}>
                          Documenti utili
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => openTool("traccia")} className={assistantPrimaryActionClass}>
                          Lascia traccia
                        </button>
                        <button
                          onClick={() => {
                            setDraft("Questa soglia va validata perché ");
                            setActiveTool("traccia");
                          }}
                          className={assistantPrimaryActionClass}
                        >
                          Valida soglia
                        </button>
                        <button onClick={() => setActiveTool("documenti")} className={assistantPrimaryActionClass}>
                          Aggiungi documento
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <textarea
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            askAssistant();
                          }
                        }}
                        rows={2}
                        placeholder="Chiedi a PRORA..."
                        className="min-w-0 flex-1 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 resize-none"
                      />
                      <button
                        onClick={() => askAssistant()}
                        disabled={chatStatus === "sending" || question.trim().length < 2}
                        className="px-4 py-2 rounded bg-slate-900 text-white dark:bg-cyan-800 hover:bg-slate-800 dark:hover:bg-cyan-700 disabled:opacity-50"
                      >
                        Invia
                      </button>
                    </div>
                    {chatError && <div className="text-sm text-rose-700 dark:text-rose-300">{chatError}</div>}

                    {activeTool === "traccia" && (
                      <div className="space-y-3 rounded border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Lascia traccia</div>
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Feedback o osservazione esperta</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setActiveTool(null)}
                            className="w-8 h-8 rounded border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                            aria-label="chiudi traccia"
                          >
                            ×
                          </button>
                        </div>
                      {!user && (
                        <div className="text-sm border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 rounded p-3">
                          Per salvare la traccia devi accedere. In fase demo puoi usare l’accesso temporaneo.
                        </div>
                      )}
                      <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={5}
                        placeholder="Scrivi la traccia, il dubbio, la correzione o il dettaglio utile."
                        className="w-full rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 resize-y"
                      />
                      <div className="flex justify-end">
                        <button
                          onClick={submitTrace}
                          disabled={!user || status === "sending" || draft.trim().length < 3}
                          className="px-4 py-2 rounded bg-slate-900 text-white dark:bg-cyan-800 hover:bg-slate-800 dark:hover:bg-cyan-700 disabled:opacity-50"
                        >
                          {status === "sending" ? "Invio..." : "Invia a PRORA"}
                        </button>
                      </div>
                    </div>
                    )}

                    {activeTool === "documenti" && (
                    <div className="space-y-4 rounded border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Documenti</div>
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Allega materiale utile alla valutazione</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setActiveTool(null)}
                          className="w-8 h-8 rounded border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                          aria-label="chiudi documenti"
                        >
                          ×
                        </button>
                      </div>
                      {!user && (
                        <div className="text-sm border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 rounded p-3">
                          Per caricare una traccia documentale devi accedere.
                        </div>
                      )}
                      <input
                        type="file"
                        onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
                        className="block w-full text-sm text-slate-700 dark:text-slate-300 file:mr-4 file:rounded file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-white dark:file:bg-cyan-800"
                      />
                      {docFile && (
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {docFile.name} · {docFile.size} byte · {docFile.type || "tipo non dichiarato"}
                        </div>
                      )}
                      <textarea
                        value={docNote}
                        onChange={(e) => setDocNote(e.target.value)}
                        rows={5}
                        placeholder="Nota facoltativa: perché questo documento è utile, cosa guardare, quali dati contiene."
                        className="w-full rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 resize-y"
                      />
                      <div className="flex justify-end">
                        <button
                          onClick={submitDocument}
                          disabled={!user || !docFile || status === "sending"}
                          className="px-4 py-2 rounded bg-slate-900 text-white dark:bg-cyan-800 hover:bg-slate-800 dark:hover:bg-cyan-700 disabled:opacity-50"
                        >
                          {status === "sending" ? "Invio..." : "Invia documento"}
                        </button>
                      </div>
                    </div>
                    )}

                    {error && <div className="mt-3 text-sm text-rose-700 dark:text-rose-300">{error}</div>}
                    {status === "ok" && <div className="mt-3 text-sm text-emerald-700 dark:text-emerald-400">Traccia salvata in bacheca.</div>}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
